import { Component, EventEmitter, Input, OnInit, Output, OnDestroy} from '@angular/core';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { BatchTransactionsService } from 'src/app/services/batchTransactions.service';
import BatchTransactionsModel from 'src/app/services/batchTransactions.model';
import { PondSectionService } from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import { CustomerPondService } from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import { FeedLogService } from 'src/app/services/feedLog.service';
import FeedLogModel from 'src/app/services/feedLog.model';
import { ItemMasterService } from 'src/app/services/itemMaster.service';
import ItemMasterModel from 'src/app/services/itemMaster.model';
import UOMMasterModel from 'src/app/services/uom.model';
import { TreatmentLogService } from 'src/app/services/treatmentLog.service';
import TreatmentLogModel from 'src/app/services/treatmentLog.model';
import { TreatmentTypeService } from 'src/app/services/treatmentType.service';
import TreatmentTypeModel from 'src/app/services/treatmentType.model';
import * as moment from 'moment';
import * as _ from 'lodash';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';

declare var $: any;

/**
 * @component AddtreatmentComponent
 * @selector app-addtreatment
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `AddtreatmentComponent` facilitates the recording of treatment applications (e.g., medicines, probiotics, feed mix)
 * to aquaculture ponds. It supports batch entry, allowing users to apply treatments to multiple ponds within a section simultaneously.
 *
 * ## Key Responsibilities:
 * 1. **Batch Treatment Entry**: Allows adding treatment logs for multiple ponds in a single transaction.
 * 2. **Hierarchical Selection**: Manages selection of Business -> Farm Site -> Section -> Ponds.
 * 3. **Autofill Capability**: Can automatically fill treatment details based on the latest batch transaction for the selected section.
 * 4. **Dynamic Form Rows**: Supports adding multiple treatment items per pond dynamically.
 * 5. **Validation**: Ensures all required fields (Item, Quantity, UOM, Type) are filled before submission.
 * 6. **Data Retrieval**: Fetches necessary master data like Treatment Types, Item Masters (Feed Mix), and UOMs.
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Can record treatments for their farm.
 *   - **Farm Manager**: Can record treatments for assigned farms.
 * - **Other Categories**:
 *   - **Employee**: Can record treatments for selected businesses.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display.
 * - `LoginUserName`: Used for tracking creator/updater.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` checks for input IDs. If present, loads dropdowns.
 * 2. **Selection**: User selects Business -> Site -> Section.
 * 3. **Pond Loading**: `getPondsBySectionId` loads ponds. It checks for the latest batch transaction and prompts to autofill.
 * 4. **Input**: User adds treatment rows (`addNewTreatmentButton`), selects items, and enters quantities.
 * 5. **Submission**: `addTreatment` validates data, constructs the batch transaction object, and sends it to the server.
 *
 * ## UI State Management:
 * - **Loading**: `responseLoader` manages loading spinners.
 * - **Editable State**: Flags like `noMealEditText`, `editButton` control the read-only/edit mode of the UI.
 * - **Dropdowns**: Manages lists for Farms, Sites, Sections, Ponds, and Treatment Items.
 */
@Component({
  selector: 'app-addtreatment',
  templateUrl: './addtreatment.component.html',
  styleUrls: ['./addtreatment.component.scss']
})
export class AddtreatmentComponent implements OnInit, OnDestroy{
  /** @type {Array<BusinessEntity>} List of available business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} Selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PondSectionModel>} List of pond sections. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<FeedLogModel>} List of feed logs (unused in current logic). */
  public feedLogs: Array<FeedLogModel>;

  /** @type {FeedLogModel} Single feed log model (unused in current logic). */
  public feedLog: FeedLogModel;

  /** @type {Array<ItemMasterModel>} List of item masters (treatment items). */
  public itemMasters: Array<ItemMasterModel>;

  /** @type {ItemMasterModel} Single item master model. */
  public itemMaster: ItemMasterModel;

  /** @type {Array<UOMMasterModel>} List of UOMs. */
  public uoms: Array<UOMMasterModel>;

  /** @type {UOMMasterModel} Single UOM model. */
  public uom: UOMMasterModel;

  /** @type {Array<TreatmentLogModel>} List of treatment logs. */
  public treatmentLogs: Array<TreatmentLogModel>;

  /** @type {TreatmentLogModel} Single treatment log model used for binding. */
  public treatmentLog: TreatmentLogModel;

  /** @type {Array<TreatmentTypeModel>} List of treatment types. */
  public treatmentTypes: Array<TreatmentTypeModel>;

  /** @type {Array<BatchTransactionsModel>} List of batch transactions. */
  public batchTransactions: Array<BatchTransactionsModel>;

  /** @type {BatchTransactionsModel} Model for the current batch transaction being created. */
  public batchTransaction: BatchTransactionsModel;

  /** @type {Array<BatchTransactionsModel>} Data of the latest batch transaction for autofill. */
  public latestBatchTransaction: Array<BatchTransactionsModel>;

  /** @type {any} Current date time. */
  public date_time: any;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag controlling the loading spinner. */
  public responseLoader: boolean;

  // /** multi select for devices **/
  /** @type {Array<any>} Dropdown list for pond details with treatment logs. */
  public pondsDetails_dropdown = new Array();
  // /** multi select for devices **/

  /** @type {any} Date of batch entry. */
  public batchEntrtyDate: any;

  /** @type {boolean} Flag indicating if values exist in the form. */
  public valueExit: boolean;

  /** @type {string} Input property for Business ID. */
  @Input('businessId') paramsbusinessId: string;

  /** @type {string} Input property for Site ID. */
  @Input('siteId') paramsfarmsiteId: string;

  /** @type {string} Input property for Section ID. */
  @Input('sectionId') paramssectionId: string;

  /** @type {string} Input property for Business Name. */
  @Input('businessName') businessName: string;

  /** @type {EventEmitter<any>} Output event to navigate back to treatment list. */
  @Output() gotoTreatmentList: EventEmitter<any> = new EventEmitter<any>();

  /** @type {Array<any>} List of treatment names (items). */
  public treatmentNames = new Array();

  /** @type {string} Time of treatment application. */
  public given_time: string;

  // UI State Flags
  public noMealEditText = true;
  public noAddIconEditable = true;
  public noTreatmentNameEditable = true;
  public nogeedQtyEditable = true;
  public noUomEditable = true;
  public noTreatmentTypeEditable = true;
  public noDescEditable = true;
  public noApplyAllPondsEditable = true;
  public noMealDeleteIconEditable = true;
  public noCardDeleteEditable = true;
  public editButton = false;
  public submitButton = false;
  public previewButton = true;

  /** @type {string} Name of the selected UOM. */
  public uomName: string;

  /** @type {boolean} Flag to disable commit button. */
  public isCommitDisabled: boolean;

  /** @type {boolean} Flag to disable download (unused). */
  public disableDownload: boolean;

  /** @type {Date} Maximum allowed date for date picker. */
  maxDate: Date = new Date();

  /**
   * Constructor for AddtreatmentComponent.
   * Initializes services and models.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {BatchTransactionsService} batchTransactionService - Service for batch transactions.
   * @param {TreatmentTypeService} treatmentTypeService - Service for treatment types.
   * @param {TreatmentLogService} treatmentLogsService - Service for treatment logs.
   * @param {ItemMasterService} itemMastersService - Service for item masters.
   * @param {FeedLogService} feedLogService - Service for feed logs.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {FarmSiteService} farmSitesService - Service for farm sites.
   * @param {PondSectionService} pondSectionService - Service for pond sections.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   *
   * @service ToastrService
   * @service BatchTransactionsService
   * @service TreatmentTypeService
   * @service TreatmentLogService
   * @service ItemMasterService
   * @service FeedLogService
   * @service CustomerPondService
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service PondSectionService
   * @service Sidenavaction
   * @service CommonMethodsService
   */
  constructor(private toasterService: ToastrService, public batchTransactionService: BatchTransactionsService, public treatmentTypeService: TreatmentTypeService, public treatmentLogsService: TreatmentLogService, public itemMastersService: ItemMasterService, public feedLogService: FeedLogService, public customerPondService: CustomerPondService, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public pondSectionService: PondSectionService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService) {
    this.treatmentLog = new TreatmentLogModel();
    this.batchTransaction = new BatchTransactionsModel();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes date picker and default values.
   * - Fetches the latest batch transaction for autofill logic.
   * - Loads initial data (treatment types, names, ponds) if input IDs are provided.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      this.responseLoader = true;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      /* user login details */
      this.date_time = new Date();
      this.given_time = moment(new Date()).format('hh:mm A');
      this.initializedatePicker();
      this.latestBatchTransaction = await this.batchTransactionService.getLatestTreatmentBatchTransaction(this.paramsbusinessId, this.paramsfarmsiteId, Number(this.paramssectionId));
      // this.TreatmentLogs = [...this.newObj];
      if (this.paramsbusinessId) {
        this.getTreatmentTypes();
        this.getTreatmentNames();
      }
      if (this.paramssectionId) {
        this.getPondsBySectionId(this.paramssectionId);
      }
      this.disableDownload = false;
      this.isCommitDisabled = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Fetches farm sites for the selected business ID (Admin/Employee flow).
   * Resets farm site ID and triggers single value check.
   * @side-effect Updates `farmSites`, `responseLoader`.
   */
  public async getAllFarmsitesBybusinessId() {
    try {
      this.paramsfarmsiteId = '';
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.paramsbusinessId);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsitesByFarmerLoginBusinessID
   * @async
   * @description Fetches farm sites for the logged-in farmer's business ID.
   * Resets farm site ID and triggers single value check.
   * @side-effect Updates `farmSites`, `responseLoader`.
   */
  public async getAllFarmsitesByFarmerLoginBusinessID() {
    try {
      this.paramsfarmsiteId = '';
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(localStorage.businessID);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one exists and triggers section fetching.
   * @side-effect Updates `paramsfarmsiteId`.
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.paramsfarmsiteId = this.farmSites[0].id; // model-binding key
      this.getSetionsByFarmsiteId(this.paramsfarmsiteId);
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Fetches sections associated with a specific farm site ID.
   * Auto-selects the section if only one exists.
   * @param {string} farmsiteid - The ID of the farm site.
   * @side-effect Updates `pondSections`, `paramssectionId`.
   */
  public async getSetionsByFarmsiteId(farmsiteid) {
    this.paramssectionId = undefined;
    this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteid);
    this.setSingleValueForSection();
  }

  /**
   * @method setSingleValueForSection
   * @description Auto-selects the section if only one exists and triggers pond fetching.
   * @side-effect Updates `paramssectionId`.
   */
  public setSingleValueForSection() {
    if (this.pondSections && this.pondSections.length == 1) {
      this.paramssectionId = this.pondSections[0].id; // model-binding key
      this.getPondsBySectionId(this.paramssectionId);
    }
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Fetches ponds for the selected section and prepares the UI for treatment entry.
   * Checks for previous batch transactions and prompts user to autofill data.
   * Populates `pondsDetails_dropdown` with pond data and potentially pre-filled treatment logs.
   * @param {string} section_id - The ID of the section.
   * @side-effect Updates `pondsDetails_dropdown`, `batchTransactions`, `responseLoader`.
   */
  public async getPondsBySectionId(section_id: string) {
    this.responseLoader = false;
    this.pondsDetails_dropdown = new Array();
    let treatmentLogData: any[];
    try {
      if (this.latestBatchTransaction && this.latestBatchTransaction.length > 0) {
        treatmentLogData = await this.treatmentLogsService.getTreatmentLogsByBatchNumber(this.latestBatchTransaction[0].id);
      }
      this.customerPonds = await this.customerPondService.getAllPondsBySectionId(section_id);
      this.pondsDetails_dropdown = new Array();
      this.responseLoader = true;
      if (this.customerPonds && this.customerPonds.length > 0) {
        for (let i = 0; i < this.customerPonds.length; i++) {
          let eachrec = this.customerPonds[i];
          if (eachrec.isPreparatory == false && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
            for (let j = 0; j < eachrec.PondCultureData.length; j++) {
              let eachpondculture = eachrec.PondCultureData[j];
              eachpondculture.pond_name = eachrec.name;
              eachpondculture.pond_id = eachrec.id;
              eachpondculture.stage = eachrec.PondUse.name;
              this.pondsDetails_dropdown.push(eachpondculture);
            }
          }
        }
      }
      /*prepare paond array objects*/
      this.batchTransactions = new Array();
      if (treatmentLogData && treatmentLogData.length > 0) {
        const batchTreatmentDataAutofilllConfirm = confirm('Auto fill from last batch entries');
        if (batchTreatmentDataAutofilllConfirm) {
          for (let i = 0;i < this.pondsDetails_dropdown.length; i++) {
            const eachPond = treatmentLogData.find((pondObj: any) => pondObj.pond_id == this.pondsDetails_dropdown[i].pond_id);
            if (eachPond) {
              let eachrec = this.pondsDetails_dropdown[i];
              eachrec.Autofill = true;
              eachrec.TreatmentLogs = new Array();
              for (let j = 0; j < eachPond.treatmentLogs.length;j++) {
                this.treatmentLog = new TreatmentLogModel();
                this.treatmentLog.treatment_item_id = eachPond.treatmentLogs[j].treatment_item_id;
                this.treatmentLog.quantity = eachPond.treatmentLogs[j].quantity;
                this.treatmentLog.treatment_type_id = eachPond.treatmentLogs[j].treatment_type_id;
                this.treatmentLog.description = eachPond.treatmentLogs[j].description;
                this.treatmentLog["treatment_type_name"] = eachPond.treatmentLogs[j].treatmentType.name;
                this.treatmentLog["treatment_name"] = eachPond.treatmentLogs[j].itemMaster.item_name;
                this.treatmentLog["business_item_id"] = eachPond.treatmentLogs[j].itemMaster.BusinessItem[0].id;
                const treatmentItemsArr = this.treatmentNames && this.treatmentNames.length > 0 ? this.treatmentNames.filter((treatmentTypeObj: any) => Number(treatmentTypeObj.id) == Number(eachPond.treatmentLogs[j].treatment_item_id)) : [];
                if (treatmentItemsArr && treatmentItemsArr.length > 0) {
                  let uomData = new Array(); // each time I prepare new items in onchange
                  if (treatmentItemsArr[0].UOMConversion && treatmentItemsArr[0].UOMConversion.length > 0) {
                    const tempUomData = [];
                    for (const eachobj of treatmentItemsArr[0].UOMConversion) {
                      const fromUOMId = eachobj.FromUOM.id;
                      const toUOMId = eachobj.ToUOM.id;
                      if ((fromUOMId != 3 && toUOMId != 3) && (fromUOMId == 1 || toUOMId == 1)) {
                        tempUomData.push({ id: 3, name: 'Gram', uomadded: true });
                      }
                      if ((fromUOMId != 12 && toUOMId != 12) && (fromUOMId == 5 || toUOMId == 5)) {
                        tempUomData.push({ id: 12, name: 'ML', uomadded: true });
                      }
                      const tempFromObj = { 'id': eachobj.from_units_uom_id, 'name': eachobj.FromUOM.name };
                      const temptoobj = { 'id': eachobj.to_units_uom_id, 'name': eachobj.ToUOM.name };
                      tempUomData.push(tempFromObj, temptoobj);
                    }
                    this.treatmentLog.itemUom = tempUomData;
                  } else {
                    uomData.push({ id: treatmentItemsArr[0].UOMMaster.id, name: treatmentItemsArr[0].UOMMaster.name });
                    this.treatmentLog.itemUom = uomData;
                  }
                }
                const uomIdsArr = this.treatmentLog.itemUom && this.treatmentLog.itemUom.length > 0 ? this.treatmentLog.itemUom.filter((uomObj: any) => uomObj.name == eachPond.treatmentLogs[j].uom) : [];
                if (uomIdsArr && uomIdsArr.length > 0) {
                  this.treatmentLog.name = uomIdsArr[0].name;
                  this.treatmentLog.uom = uomIdsArr[0].name;
                  this.treatmentLog.isManualUOM = uomIdsArr[0].uomadded ? true : false;
                  this.uomName = uomIdsArr[0].name;
                  this.treatmentLog.uom_id = Number(uomIdsArr[0].id);
                } else {
                  this.treatmentLog.name = null;
                }
                eachrec.TreatmentLogs.push(this.treatmentLog);
                this.batchTransaction.TreatmentLogs.push(this.treatmentLog);
              }
            } else {
              let eachrec = this.pondsDetails_dropdown[i];
              eachrec.Autofill = false;
              eachrec.TreatmentLogs = new Array();
              this.treatmentLog = new TreatmentLogModel();
              eachrec.TreatmentLogs.push(this.treatmentLog);
              this.batchTransaction.TreatmentLogs.push(this.treatmentLog);
            }
          }
        } else {
          this.commonSectionPonds();
        }
      } else {
        this.commonSectionPonds();
      }
      /*prepare paond array objects end*/
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method commonSectionPonds
   * @description Initializes empty treatment logs for all ponds in the dropdown list.
   * Used when autofill is declined or no previous data exists.
   */
  public commonSectionPonds() {
    for (let k = 0; k < this.pondsDetails_dropdown.length; k++) {
      let eachrec = this.pondsDetails_dropdown[k];
      eachrec.Autofill = false;
      eachrec.TreatmentLogs = new Array();
      this.treatmentLog = new TreatmentLogModel();
      eachrec.TreatmentLogs.push(this.treatmentLog);
      this.batchTransaction.TreatmentLogs.push(this.treatmentLog);
    }
  }

  /**
   * @method getTreatmentNames
   * @async
   * @description Fetches available treatment items (Feed Mix/Medicines) for the business.
   * Formats item names with subtypes and UOM conversions for display.
   * @side-effect Updates `treatmentNames`.
   */
  public async getTreatmentNames() {
    this.businessEntity = JSON.parse(localStorage.getItem('businessEntityById'));
    if (this.businessEntity.providerID.length > 0) {
      this.paramsbusinessId = this.businessEntity.providerID[0].businessID;
    } else {
      this.paramsbusinessId = this.businessEntity.id;
    }
    try {
      this.itemMasters = await this.itemMastersService.getFeedMix(this.paramsbusinessId);
      this.treatmentNames = this.itemMasters;
      for (let k = 0; k < this.treatmentNames.length; k++) {
        let eachItem = this.treatmentNames[k];
        this.treatmentNames[k].item_name = eachItem.item_name + '' + (eachItem.sub_type ? '-' + eachItem.sub_type : '') + '' + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) ? '-' + (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) : '') + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) ? (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) : '');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getTreatmentNameIdsUomData
   * @description Prepares UOM data for a selected treatment item in a specific row.
   * @param {number} index - The index of the treatment log row.
   * @param {number} j - The index of the pond.
   * @side-effect Updates `pondsDetails_dropdown[j].TreatmentLogs[index].itemUom`, calls `prepareUOM`.
   */
  public getTreatmentNameIdsUomData(index: number, j: number) {
    this.pondsDetails_dropdown[j].TreatmentLogs[index].itemUom = new Array();
    let treatmentNameIdarr = _.filter(this.treatmentNames, (o) => {
      return o.id == this.pondsDetails_dropdown[j].TreatmentLogs[index].treatment_item_id;
    });
    if (treatmentNameIdarr.length > 0) {
      this.pondsDetails_dropdown[j].TreatmentLogs[index].treatment_item_id = treatmentNameIdarr[0].id;
      this.prepareUOM(treatmentNameIdarr[0], index, j);
    }
  }

  /**
   * @method prepareUOM
   * @description Prepares the UOM dropdown list for a specific treatment item based on conversions.
   * Adds 'Gram' and 'ML' options if applicable.
   * @param {any} itemsArray - The item master object.
   * @param {number} index - The index of the treatment log row.
   * @param {number} j - The index of the pond.
   */
  public prepareUOM(itemsArray: any, index: number, j: number) {
    let uomData = new Array(); // each time i prepare new items in onchange
    if (itemsArray && itemsArray.UOMConversion && itemsArray.UOMConversion.length > 0) {
      let uomdataArr = itemsArray.UOMConversion;
      for (let i = 0; i < uomdataArr.length; i++) {
        let eachobj = uomdataArr[i];
        if (eachobj['FromUOM']['id'] != 3 && eachobj['ToUOM']['id'] != 3 && (eachobj['FromUOM']['id'] == 1 || eachobj['ToUOM']['id'] == 1)) {
          uomData.push({ id: 3, name: 'Gram', 'uomadded': true });
        }
        if (eachobj['FromUOM']['id'] != 12 && eachobj['ToUOM']['id'] != 12 && (eachobj['FromUOM']['id'] == 5 || eachobj['ToUOM']['id'] == 5)) {
          uomData.push({ id: 12, name: 'ML', 'uomadded': true });
        }
        let tempFromObj = {
          'id': eachobj.from_units_uom_id,
          'name': eachobj.FromUOM.name
        };
        let temptoobj = {
          'id': eachobj.to_units_uom_id,
          'name': eachobj.ToUOM.name
        };
        uomData.push(tempFromObj);
        uomData.push(temptoobj);
        this.pondsDetails_dropdown[j].TreatmentLogs[index].itemUom = uomData;
      }
    } else {
      uomData.push({ id: itemsArray['UOMMaster']['id'], name: itemsArray['UOMMaster']['name'] });
      this.pondsDetails_dropdown[j].TreatmentLogs[index].itemUom = uomData;
    }
  }


  /**
   * @method getTreatmentTypes
   * @async
   * @description Fetches all available treatment types (e.g., Probiotic, Mineral).
   */
  public async getTreatmentTypes() {
    try {
      this.treatmentTypes = await this.treatmentTypeService.getAll();
      for (let i = 0; i < this.treatmentTypes.length; i++) {
        if (this.treatmentTypes[i] && this.treatmentTypes[i].others_name != null) {
          this.treatmentTypes[i].name = this.treatmentTypes[i].name + ',' + this.treatmentTypes[i].others_name;
        } else {
          this.treatmentTypes[i].name = this.treatmentTypes[i] && this.treatmentTypes[i].name;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method initializedatePicker
   * @description Initializes the jQuery datepicker for the batch entry date.
   */
  public initializedatePicker() {
    // let that = this;
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function (selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        // that.setEndDatebyStartDate(selectedDate);
        $('#startdate').val(parsedDate);
      }
    });
  }

  /**
   * @method addNewTreatmentButton
   * @description Adds a new treatment log row for a specific pond.
   * Validates the last row before adding a new one.
   * @param {number} j - The index of the pond.
   */
  public addNewTreatmentButton(j: number) {
    if (this.pondsDetails_dropdown[j].TreatmentLogs.length > 0) {
      if (this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['treatment_item_id'] == undefined) {
        this.toasterService.error('Select treatment', '', {
          timeOut: 2000
        });
        return;
      } else if (this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['quantity'] == null || this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['quantity'] == '' ||
      this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['quantity'] == '-' ||
      this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['quantity'] == 0) {
        this.toasterService.error('Enter quantity', '', {
          timeOut: 2000
        });
        return;
      } else if (this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['uom_id'] == undefined) {
        this.toasterService.error('Select UOM', '', {
          timeOut: 2000
        });
        return;
      } else if (this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['treatment_type_id'] == undefined) {
        this.toasterService.error('Select treatment type', '', {
          timeOut: 2000
        });
        return;
      } else if (this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1]['description'] == null) {
        this.toasterService.error('Enter description', '', {
          timeOut: 2000
        });
        return;
      }
    }
    if (this.pondsDetails_dropdown[j].TreatmentLogs.length > 0) {
      this.pondsDetails_dropdown[j].TreatmentLogs.push(JSON.parse(JSON.stringify(this.pondsDetails_dropdown[j].TreatmentLogs[this.pondsDetails_dropdown[j].TreatmentLogs.length - 1])));
    }
  }

  /**
   * @method applyToAllPonds
   * @description Copies the treatment logs from a specific pond to all other ponds in the list.
   * @param {number} j - The index of the source pond.
   */
  public applyToAllPonds(j: number) {
    try {
      let TreatmentLogs = JSON.parse(JSON.stringify(this.pondsDetails_dropdown[j].TreatmentLogs));
      this.pondsDetails_dropdown.filter(pond => {
        pond.TreatmentLogs = new Array();
        for (let i = 0; i < TreatmentLogs.length; i++) {
          pond.TreatmentLogs.push(JSON.parse(JSON.stringify(TreatmentLogs[i])));
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method removeObj
   * @description Removes a treatment log row from a specific pond.
   * @param {number} i - The index of the row to remove.
   * @param {number} j - The index of the pond.
   * @returns {boolean} True if removed, false otherwise.
   */
  public removeObj(i: number, j: number) {
    if (this.pondsDetails_dropdown[j].TreatmentLogs.length == 1) {
      this.toasterService.warning('Can\'t delete the row when there is only one row');
      return false;
    } else {
      this.pondsDetails_dropdown[j].TreatmentLogs.splice(i, 1);
      this.toasterService.success('Row deleted successfully', '', {
        timeOut: 2000
      });
      return true;
    }
  }

  /**
   * @method removePond
   * @description Removes a pond card from the list.
   * @param {number} i - The index of the pond to remove.
   * @returns {boolean} True if removed, false otherwise.
   */
  public removePond(i: number) {
    if (this.pondsDetails_dropdown.length == 1) {
      this.toasterService.warning('Can\'t delete the pond card when there is only one pond');
      return false;
    } else {
      this.pondsDetails_dropdown.splice(i, 1);
      this.toasterService.success('Card deleted successfully', '', {
        timeOut: 2000
      });
      return true;
    }
  }

  /**
   * @method treatmentNameById
   * @description Finds and sets the treatment name based on the selected treatment item ID.
   * @param {number} index - The index of the treatment log row.
   * @param {number} j - The index of the pond.
   * @side-effect Updates `treatment_name` in the model.
   */
  public treatmentNameById(index: number, j: number) {
    let treatmentNameIdarr = _.filter(this.treatmentNames, (o) => {
      return o.id == this.pondsDetails_dropdown[j].TreatmentLogs[index].treatment_item_id;
    });
    if (treatmentNameIdarr.length > 0) {
      this.pondsDetails_dropdown[j].TreatmentLogs[index].treatment_name = treatmentNameIdarr[0].item_name;
    }
  }

  /**
   * @method getUomNameByUomId
   * @description Finds and sets the UOM name based on the selected UOM ID.
   * @param {number} index - The index of the treatment log row.
   * @param {number} j - The index of the pond.
   * @param {number} selectedUOMId - The selected UOM ID.
   * @side-effect Updates `uom`, `name`, `isManualUOM` in the model.
   */
  public getUomNameByUomId(index: number, j: number, selectedUOMId: number) {
    this.pondsDetails_dropdown[j].TreatmentLogs[index].uom_id = selectedUOMId;
    let uomIdarr = _.filter(this.pondsDetails_dropdown[j].TreatmentLogs[index].itemUom, (o) => {
      return o.id == this.pondsDetails_dropdown[j].TreatmentLogs[index].uom_id;
    });
    if (uomIdarr.length > 0) {
      this.pondsDetails_dropdown[j].TreatmentLogs[index].name = uomIdarr[0].name;
      this.pondsDetails_dropdown[j].TreatmentLogs[index].uom = uomIdarr[0].name;
      this.pondsDetails_dropdown[j].TreatmentLogs[index].isManualUOM = uomIdarr[0].uomadded ? true : false;
      this.uomName = this.pondsDetails_dropdown[j].TreatmentLogs[index].name;
    } else {
      this.pondsDetails_dropdown[j].TreatmentLogs[index].name = null;
    }
  }

  /**
   * @method getTreatmentTypeNameById
   * @description Finds and sets the treatment type name based on the selected type ID.
   * @param {number} i - The index of the treatment log row.
   * @param {number} j - The index of the pond.
   * @side-effect Updates `treatment_type_name` in the model.
   */
  public getTreatmentTypeNameById(i: number, j: number) {
    let treatmentTypeNameIdarr = _.filter(this.treatmentTypes, (o) => {
      return o.id == this.pondsDetails_dropdown[j].TreatmentLogs[i].treatment_type_id;
    });
    if (treatmentTypeNameIdarr.length > 0) {
      this.pondsDetails_dropdown[j].TreatmentLogs[i].treatment_type_name = treatmentTypeNameIdarr[0].name;
    }
  }

  /**
   * @method priviewButton
   * @description Validates all treatment entries and switches the UI to preview/edit mode.
   * Checks for missing required fields across all ponds and rows.
   */
  public priviewButton() {
    let mealsEntered = 0; // the meals data
    let noOfMeals = 0; // TreatmentLogs.length where feed_item_id or feed_qty_given or uom_id is entered
    for (let i = 0; i < this.pondsDetails_dropdown.length; i++) {
      if (!this.paramsbusinessId) {
        this.toasterService.error('Please select farm', '', {
          timeOut: 2000
        });
        return false;
      }
      if (!this.paramsfarmsiteId) {
        this.toasterService.error('Please select farmsite', '', {
          timeOut: 2000
        });
        return false;
      }
      if (!this.paramssectionId) {
        this.toasterService.error('Please select section', '', {
          timeOut: 2000
        });
        return false;
      }
      if (!this.pondsDetails_dropdown[i].pond_id) {
        this.toasterService.error('Please select ponds', '', {
          timeOut: 2000
        });
        return false;
      }
      for (let j = 0; j < this.pondsDetails_dropdown[i].TreatmentLogs.length; j++) {
        let eachObj = this.pondsDetails_dropdown[i].TreatmentLogs[j];
        if (eachObj.quantity == '') {
          this.toasterService.error('Enter quantity', '', {
            timeOut: 2000
          });
          return false;
        }
        if (eachObj.quantity == 0 || eachObj.quantity == '-') {
          this.toasterService.error('Enter valid quantity', '', {
            timeOut: 2000
          });
          return false;
        }
        if (eachObj.treatment_item_id != undefined || eachObj.quantity != undefined || eachObj.uom_id != undefined || eachObj.treatment_type_id != undefined || eachObj.description != null) {
          noOfMeals += 1;
        }
        if (eachObj.treatment_item_id != undefined && eachObj.quantity != undefined && eachObj.uom_id != undefined && eachObj.treatment_type_id != undefined && eachObj.description != null) {
          this.valueExit = true;
          mealsEntered += 1;
          // break;
        } else {
          this.valueExit = false;
        }
      }
    }
    if ((mealsEntered == 0) || (mealsEntered != noOfMeals)) {
      this.toasterService.error('Provide treatment data', '', {
        timeOut: 2000
      });
      return;
    }
    this.noTreatmentNameEditable = false;
    this.nogeedQtyEditable = false;
    this.noAddIconEditable = false;
    this.noUomEditable = false;
    this.noTreatmentTypeEditable = false;
    this.noDescEditable = false;
    this.noMealDeleteIconEditable = false;
    this.noApplyAllPondsEditable = false;
    this.noCardDeleteEditable = false;
    this.editButton = true;
    this.submitButton = true;
    this.previewButton = false;
  }

  /**
   * @method treatmentEditIcon
   * @description Switches the UI back to edit mode from preview mode.
   * Enables input fields and action buttons.
   */
  public treatmentEditIcon() {
    this.noAddIconEditable = true;
    this.noTreatmentNameEditable = true;
    this.nogeedQtyEditable = true;
    this.noUomEditable = true;
    this.noTreatmentTypeEditable = true;
    this.noDescEditable = true;
    this.noMealDeleteIconEditable = true;
    this.noApplyAllPondsEditable = true;
    this.noCardDeleteEditable = true;
    this.submitButton = false;
    this.editButton = false;
    this.previewButton = true;
  }

  /**
   * @method getTreatmentBusinessItemId
   * @description Finds and sets the business item ID for a selected treatment item.
   * @param {number} index - The index of the treatment log row.
   * @param {number} j - The index of the pond.
   * @side-effect Updates `business_item_id` in the model.
   */
  public getTreatmentBusinessItemId(index: number, j: number) {
    let businessItemIdarr = _.filter(this.treatmentNames, (o) => {
      return o.id == this.pondsDetails_dropdown[j].TreatmentLogs[index].treatment_item_id;
    });
    if (businessItemIdarr.length > 0) {
      this.pondsDetails_dropdown[j].TreatmentLogs[index].business_item_id = businessItemIdarr[0].BusinessItem[0].id;
    }
  }

  /**
   * @method numberValidation
   * @description Validates input to allow only numbers and a single decimal point.
   * Prevents invalid characters.
   * @param {any} event - The keyboard event.
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    const cursorPosition = event.target.selectionStart;
    // Allow numbers, the dot (decimal point), the minus sign, and the Backspace key
    if (
      !/^[\d.-]$/.test(key) && // Check if the entered character is a number, dot, or minus sign
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot or a minus sign after the first character
    if ((key === '.' && value == '' && cursorPosition === 0) || (key === '.' && value.includes('.'))
    || (key === '-' && value != '' && cursorPosition != 0) || (key === '.' && value.includes('-') && cursorPosition == 1)
    || (key === '.' && value != '' && cursorPosition === 0))
    {
      event.preventDefault();
    }
  }

  /**
   * @method formatDateToYMD
   * @description Formats a date object to YYYY-MM-DD string.
   * @param {any} date - The date to format.
   * @returns {string} Formatted date string.
   */
  formatDateToYMD = (date: any): string => {
    const d = new Date(date); // Convert to Date object
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * @method addTreatment
   * @async
   * @description Validates and submits the batch treatment transaction.
   * Constructs the batch transaction object with all treatment logs and sends it to the server.
   * @side-effect Updates `batchTransaction`, emits `gotoTreatmentList` on success.
   */
  public async addTreatment() {
    try {
      if (!this.paramsbusinessId) {
        this.toasterService.error('Please select farm', '', {
          timeOut: 2000
        });
        return false;
      }
      if (!this.paramsfarmsiteId) {
        this.toasterService.error('Please select farmsite', '', {
          timeOut: 2000
        });
        return false;
      }
      if (!this.paramssectionId) {
        this.toasterService.error('Please select section', '', {
          timeOut: 2000
        });
        return false;
      }
      for (let i = 0; i < this.pondsDetails_dropdown.length; i++) {
        if (!this.pondsDetails_dropdown[i].pond_id) {
          this.toasterService.error('Please select ponds', '', {
            timeOut: 2000
          });
          return false;
        }
      }
      const formattedDate = this.formatDateToYMD(this.date_time);
      const formatedTime = moment(this.given_time, ['h:mm A']).format('HH:mm');
      this.batchTransaction.batch_type = 'Treatment';
      this.batchTransaction.farm_site_id = this.paramsfarmsiteId;
      this.batchTransaction.date_time = formattedDate + ' ' + formatedTime;
      this.batchTransaction.section_id = Number(this.paramssectionId);
      let count = -1;
      let treatmentLogsList = new Array();
      for (let i = 0; i <= this.pondsDetails_dropdown.length - 1; i++) {
        let eachMydata = JSON.parse(JSON.stringify(this.pondsDetails_dropdown[i].TreatmentLogs));
        for (let j = 0; j <= eachMydata.length - 1; j++) {
          let eachObj = JSON.parse(JSON.stringify(eachMydata[j]));
          let item = this.treatmentNames.find(selectedItem => selectedItem.id == Number(this.pondsDetails_dropdown[i].TreatmentLogs[j].treatment_item_id))
          if (eachObj.treatment_item_id && eachObj.treatment_item_id != -1) {
            count++;
            const formatedTime = this.given_time = moment(this.given_time, ['h:mm A']).format('HH:mm');

            let uomid = eachObj.uom_id;
            let qty = eachObj.quantity;
            let uomName = eachObj.uom;

            eachObj.pond_id = this.pondsDetails_dropdown[i].pond_id;
            eachObj.stage = this.pondsDetails_dropdown[i].stage;
            eachObj.doc = this.pondsDetails_dropdown[i].doc;
            eachObj.pond_culture_id = this.pondsDetails_dropdown[i].id;
            eachObj.business_id = this.paramsbusinessId;
            eachObj.notificationData = null;
            eachObj.transaction_type_id = 3;
            eachObj.isPreparatory = false;
            eachObj.date = formattedDate;
            eachObj.time = formatedTime;
            eachObj.treatment_type_id = Number(this.pondsDetails_dropdown[i].TreatmentLogs[j].treatment_type_id);
            eachObj.description = this.pondsDetails_dropdown[i].TreatmentLogs[j].description;
            eachObj.quantity = qty;
            eachObj.item_id = Number(this.pondsDetails_dropdown[i].TreatmentLogs[j].treatment_item_id);
            eachObj.treatment_item_id = Number(this.pondsDetails_dropdown[i].TreatmentLogs[j].treatment_item_id);
            eachObj.uom_id = Number(uomid);
            eachObj.uom = uomName;
            eachObj.from_value = item.UOMConversion[0].from_value;
            eachObj.from_units_uom_id = item.UOMConversion[0].FromUOM.id;
            eachObj.to_value = item.UOMConversion[0].to_value;
            eachObj.to_units_uom_id = item.UOMConversion[0].ToUOM.id;
            eachObj.business_item_id = (eachObj.business_item_id != '-1' && eachObj.business_item_id != '') ? eachObj.business_item_id : null;
            eachObj.farm_site_id = this.paramsfarmsiteId;
            eachObj.person_id = localStorage.personID;
            treatmentLogsList[count] = (eachObj);
          }
        }
      }
      this.batchTransaction['TreatmentLogs'] = treatmentLogsList;
      if (this.batchTransaction) {
        this.isCommitDisabled = true;
        let batchTreatmentLog = await this.batchTransactionService.addTreatmentLog(this.batchTransaction);
        if (batchTreatmentLog) {
          this.isCommitDisabled = false;
          this.disableDownload = false;
          this.toasterService.success('Treatment created successfully', '', {
            timeOut: 2000
          });
          this.gotoTreatmentList.emit(true);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
